import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Calendar, User, Phone, FileText, Clock, CheckCircle, XCircle, UserPlus, RefreshCw, Search, CalendarCheck, Users, AlertTriangle, Filter, Mail, CheckSquare } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Bekliyor', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'İptal', color: 'bg-red-100 text-red-800', icon: XCircle },
};

export default function SecondVisit() {
  const { getAuthHeader, isAdmin, user } = useAuth();
  
  // Sales records from Google Sheets (filtered for current staff)
  const [salesRecords, setSalesRecords] = useState([]);
  const [loadingSales, setLoadingSales] = useState(true);
  
  // 2. Visit records
  const [visits, setVisits] = useState([]);
  const [loadingVisits, setLoadingVisits] = useState(true);
  
  // Users list for admin filter
  const [users, setUsers] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('all');
  
  // UI States
  const [searchTerm, setSearchTerm] = useState('');
  const [markDialogOpen, setMarkDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [visitNotes, setVisitNotes] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Bulk selection states
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkNotes, setBulkNotes] = useState('');
  
  // Email reminder states
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Notes dialog for existing visits
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [editNotes, setEditNotes] = useState('');

  // Fetch users list for admin
  const fetchUsers = useCallback(async () => {
    if (!isAdmin()) return;
    try {
      const response = await axios.get(`${API_URL}/api/users`, getAuthHeader());
      // Filter only staff users
      const staffUsers = response.data.filter(u => u.role === 'staff');
      setUsers(staffUsers);
    } catch (error) {
      console.error('Users fetch error:', error);
    }
  }, [getAuthHeader, isAdmin]);

  // Fetch staff's sales records
  const fetchSalesRecords = useCallback(async () => {
    try {
      setLoadingSales(true);
      const response = await axios.get(`${API_URL}/api/sales-records`, getAuthHeader());
      // Filter only non-cancelled, active records
      const filtered = response.data.filter(r => !r.is_cancelled);
      setSalesRecords(filtered);
    } catch (error) {
      toast.error('Satış kayıtları yüklenemedi');
    } finally {
      setLoadingSales(false);
    }
  }, [getAuthHeader]);

  // Fetch 2. Visit records
  const fetchVisits = useCallback(async () => {
    try {
      setLoadingVisits(true);
      const response = await axios.get(`${API_URL}/api/second-visits`, getAuthHeader());
      setVisits(response.data);
    } catch (error) {
      toast.error('2. Visit kayıtları yüklenemedi');
    } finally {
      setLoadingVisits(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchSalesRecords();
    fetchVisits();
    fetchUsers();
  }, [fetchSalesRecords, fetchVisits, fetchUsers]);

  // Check if a sales record is already marked as 2nd visit
  const isMarkedAsSecondVisit = (record) => {
    return visits.some(v => 
      v.patient_name === record.patient_name || 
      (v.sale_id && v.sale_id === record.id)
    );
  };

  // Get visit record for a sales record
  const getVisitForRecord = (record) => {
    return visits.find(v => 
      v.patient_name === record.patient_name || 
      (v.sale_id && v.sale_id === record.id)
    );
  };

  // Mark a sales record as 2nd visit
  const handleMarkAsSecondVisit = async () => {
    if (!selectedRecord) return;
    
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/second-visits`, {
        patient_name: selectedRecord.patient_name,
        patient_phone: selectedRecord.patient_phone || '',
        treatment_type: selectedRecord.treatment || '',
        notes: visitNotes,
        visit_date: selectedRecord.ticket_date || selectedRecord.treatment_date || new Date().toISOString().split('T')[0],
        sale_id: selectedRecord.id,
        status: 'pending'
      }, getAuthHeader());
      
      toast.success(`${selectedRecord.patient_name} 2. Visit olarak işaretlendi`);
      setMarkDialogOpen(false);
      setSelectedRecord(null);
      setVisitNotes('');
      fetchVisits();
    } catch (error) {
      toast.error('İşaretleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  // Update visit notes
  const handleUpdateNotes = async () => {
    if (!selectedVisit) return;
    
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/second-visits/${selectedVisit.id}`, {
        notes: editNotes
      }, getAuthHeader());
      
      toast.success('Notlar güncellendi');
      setNotesDialogOpen(false);
      setSelectedVisit(null);
      setEditNotes('');
      fetchVisits();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  // Update visit status
  const handleUpdateStatus = async (visitId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/second-visits/${visitId}`, {
        status: newStatus
      }, getAuthHeader());
      
      toast.success('Durum güncellendi');
      fetchVisits();
    } catch (error) {
      toast.error('Durum güncellenemedi');
    }
  };

  // Remove from 2nd visit list
  const handleRemoveFromSecondVisit = async (visitId) => {
    try {
      await axios.delete(`${API_URL}/api/second-visits/${visitId}`, getAuthHeader());
      toast.success('2. Visit kaydı silindi');
      fetchVisits();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  // Toggle record selection for bulk operations
  const toggleRecordSelection = (recordId) => {
    setSelectedRecords(prev => {
      if (prev.includes(recordId)) {
        return prev.filter(id => id !== recordId);
      }
      return [...prev, recordId];
    });
  };

  // Select all unmarked records
  const selectAllUnmarked = () => {
    const unmarkedIds = filteredSalesRecords
      .filter(r => !isMarkedAsSecondVisit(r))
      .map(r => r.id);
    setSelectedRecords(unmarkedIds);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedRecords([]);
  };

  // Bulk mark as 2nd visit
  const handleBulkMarkAsSecondVisit = async () => {
    if (selectedRecords.length === 0) return;
    
    setSaving(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const recordId of selectedRecords) {
      const record = salesRecords.find(r => r.id === recordId);
      if (!record || isMarkedAsSecondVisit(record)) continue;
      
      try {
        await axios.post(`${API_URL}/api/second-visits`, {
          patient_name: record.patient_name,
          patient_phone: record.patient_phone || '',
          treatment_type: record.treatment || '',
          notes: bulkNotes,
          visit_date: record.ticket_date || record.treatment_date || new Date().toISOString().split('T')[0],
          sale_id: record.id,
          status: 'pending'
        }, getAuthHeader());
        successCount++;
      } catch (error) {
        failCount++;
      }
    }
    
    if (successCount > 0) {
      toast.success(`${successCount} hasta 2. Visit olarak işaretlendi`);
    }
    if (failCount > 0) {
      toast.error(`${failCount} hasta işaretlenemedi`);
    }
    
    setBulkDialogOpen(false);
    setSelectedRecords([]);
    setBulkNotes('');
    setSaving(false);
    fetchVisits();
  };

  // Send email reminder for overdue patients
  const handleSendEmailReminder = async () => {
    if (overdueVisits.length === 0) {
      toast.error('Hatırlatma gönderilecek hasta yok');
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API_URL}/api/second-visits/send-reminder`, {
        visit_ids: overdueVisits.map(v => v.id)
      }, getAuthHeader());
      
      toast.success(response.data.message || `${overdueVisits.length} hasta için hatırlatma gönderildi`);
    } catch (error) {
      toast.error('Hatırlatma gönderilemedi: ' + (error.response?.data?.detail || 'Bir hata oluştu'));
    } finally {
      setSendingEmail(false);
    }
  };

  // Calculate days since visit date
  const getDaysSinceVisit = (visitDate) => {
    if (!visitDate) return 0;
    const visit = new Date(visitDate);
    const now = new Date();
    const diffTime = Math.abs(now - visit);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Filter visits by selected staff (for admin)
  const getFilteredVisits = () => {
    if (!isAdmin() || selectedStaff === 'all') {
      return visits;
    }
    return visits.filter(v => v.created_by === selectedStaff);
  };

  // Get staff statistics
  const getStaffStats = () => {
    const stats = {};
    visits.filter(v => v.status === 'pending').forEach(v => {
      const staff = v.created_by || 'Bilinmiyor';
      if (!stats[staff]) {
        stats[staff] = { count: 0, overdue: 0 };
      }
      stats[staff].count++;
      if (getDaysSinceVisit(v.visit_date) >= 90) {
        stats[staff].overdue++;
      }
    });
    return stats;
  };

  const filteredVisits = getFilteredVisits();
  const staffStats = isAdmin() ? getStaffStats() : {};

  // Filter sales records by search term
  const filteredSalesRecords = salesRecords.filter(r => {
    const searchLower = searchTerm.toLowerCase();
    return (
      r.patient_name?.toLowerCase().includes(searchLower) ||
      r.treatment?.toLowerCase().includes(searchLower) ||
      r.ticket_date?.includes(searchTerm)
    );
  });

  // Group visits by status
  const pendingVisits = filteredVisits.filter(v => v.status === 'pending');
  const completedVisits = filteredVisits.filter(v => v.status === 'completed');
  
  // 3+ months overdue visits
  const overdueVisits = filteredVisits.filter(v => 
    v.status === 'pending' && getDaysSinceVisit(v.visit_date) >= 90
  );

  const loading = loadingSales || loadingVisits;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="second-visit-loading">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="second-visit-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 p-8 text-white">
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        
        <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-[Manrope] flex items-center gap-3">
              <CalendarCheck className="h-8 w-8" />
              2. Visit Takip
            </h1>
            <p className="mt-2 text-white/80">
              {isAdmin() ? 'Tüm personellerin 2. visit kayıtları' : 'Hastalarınızı 2. visit olarak işaretleyin'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Admin Staff Filter */}
            {isAdmin() && (
              <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                <SelectTrigger className="w-[200px] bg-white/20 border-white/30 text-white">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Personel Filtrele" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Personeller</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.email}>
                      {u.name || u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button 
              variant="secondary" 
              onClick={() => { fetchSalesRecords(); fetchVisits(); }}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Yenile
            </Button>
          </div>
        </div>
      </div>

      {/* Admin: Staff Statistics */}
      {isAdmin() && Object.keys(staffStats).length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="font-[Manrope] flex items-center gap-2 text-lg">
              <Users className="h-5 w-5 text-primary" />
              Personel Bazında Bekleyen 2. Visitler
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {Object.entries(staffStats).map(([staff, data]) => (
                <div 
                  key={staff}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedStaff === staff 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:bg-muted/50'
                  }`}
                  onClick={() => setSelectedStaff(staff)}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium truncate">{staff.split('@')[0]}</span>
                    <Badge variant="secondary" className="ml-2">
                      {data.count}
                    </Badge>
                  </div>
                  {data.overdue > 0 && (
                    <div className="flex items-center gap-1 mt-1 text-xs text-red-600">
                      <AlertTriangle className="h-3 w-3" />
                      {data.overdue} adet 3+ ay
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 3+ Months Overdue Section */}
      {overdueVisits.length > 0 && (
        <Card className="border-red-200 bg-red-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-[Manrope] flex items-center gap-2 text-lg text-red-700">
                <AlertTriangle className="h-5 w-5" />
                3 Ayı Geçen Hastalar
                <Badge variant="destructive" className="ml-2">{overdueVisits.length}</Badge>
              </CardTitle>
              {isAdmin() && (
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-2 border-red-300 text-red-700 hover:bg-red-100"
                  onClick={handleSendEmailReminder}
                  disabled={sendingEmail}
                >
                  <Mail className="h-4 w-4" />
                  {sendingEmail ? 'Gönderiliyor...' : 'Hatırlatma E-posta Gönder'}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[250px] overflow-y-auto">
              {overdueVisits.map((visit) => {
                const daysSince = getDaysSinceVisit(visit.visit_date);
                const monthsSince = Math.floor(daysSince / 30);
                
                return (
                  <div 
                    key={visit.id}
                    className="p-3 rounded-lg bg-white border border-red-200"
                    data-testid={`overdue-visit-${visit.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium">{visit.patient_name}</span>
                        <p className="text-xs text-muted-foreground mt-1">
                          {visit.treatment_type || '-'} • İlk tarih: {visit.visit_date || '-'}
                        </p>
                        <p className="text-xs text-red-600 font-medium mt-1">
                          {monthsSince} ay {daysSince % 30} gün geçti
                        </p>
                        {isAdmin() && (
                          <p className="text-xs text-muted-foreground">
                            Personel: {visit.created_by?.split('@')[0] || '-'}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => handleUpdateStatus(visit.id, 'completed')}
                          title="Tamamlandı"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleRemoveFromSecondVisit(visit.id)}
                          title="Kaldır"
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: Sales Records to Mark */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="font-[Manrope] flex items-center gap-2 text-lg">
              <User className="h-5 w-5 text-primary" />
              Satış Kayıtları
              {selectedRecords.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {selectedRecords.length} seçili
                </Badge>
              )}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              2. Visit olarak işaretlemek istediğiniz hastaları seçin
            </p>
            
            {/* Search and Bulk Actions */}
            <div className="flex flex-col gap-3 mt-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Hasta adı veya tedavi ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-sales-input"
                />
              </div>
              
              {/* Bulk Action Buttons */}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={selectAllUnmarked}
                  className="gap-1"
                >
                  <CheckSquare className="h-4 w-4" />
                  Tümünü Seç
                </Button>
                {selectedRecords.length > 0 && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={clearSelection}
                    >
                      Seçimi Temizle
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setBulkDialogOpen(true)}
                      className="gap-1 bg-primary"
                    >
                      <UserPlus className="h-4 w-4" />
                      Toplu İşaretle ({selectedRecords.length})
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {filteredSalesRecords.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <User className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Satış kaydı bulunamadı</p>
                </div>
              ) : (
                filteredSalesRecords.slice(0, 50).map((record) => {
                  const isMarked = isMarkedAsSecondVisit(record);
                  const visitRecord = getVisitForRecord(record);
                  const isSelected = selectedRecords.includes(record.id);
                  
                  return (
                    <div 
                      key={record.id}
                      className={`p-3 rounded-lg border transition-colors ${
                        isMarked 
                          ? 'bg-green-50 border-green-200' 
                          : isSelected
                            ? 'bg-primary/10 border-primary'
                            : 'bg-muted/30 border-border hover:bg-muted/50'
                      }`}
                      data-testid={`sales-record-${record.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {/* Checkbox for bulk selection */}
                        {!isMarked && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleRecordSelection(record.id)}
                            className="flex-shrink-0"
                          />
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate">{record.patient_name}</span>
                            {isMarked && (
                              <Badge variant="success" className="bg-green-100 text-green-800 text-xs">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                2. Visit
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {record.ticket_date || record.treatment_date || '-'}
                            </span>
                            <span>{record.treatment || '-'}</span>
                          </div>
                        </div>
                        
                        {!isMarked ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedRecord(record);
                              setMarkDialogOpen(true);
                            }}
                            className="ml-2 gap-1"
                            data-testid={`mark-second-visit-btn-${record.id}`}
                          >
                            <UserPlus className="h-4 w-4" />
                            İşaretle
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedVisit(visitRecord);
                              setEditNotes(visitRecord?.notes || '');
                              setNotesDialogOpen(true);
                            }}
                            className="ml-2 gap-1 text-muted-foreground"
                          >
                            <FileText className="h-4 w-4" />
                            Not Ekle
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
              {filteredSalesRecords.length > 50 && (
                <p className="text-sm text-center text-muted-foreground py-2">
                  İlk 50 kayıt gösteriliyor ({filteredSalesRecords.length} toplam)
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Right: 2. Visit List */}
        <div className="space-y-6">
          {/* Pending Visits */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="font-[Manrope] flex items-center gap-2 text-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
                Bekleyen 2. Visitler
                <Badge variant="outline" className="ml-auto">{pendingVisits.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[250px] overflow-y-auto">
                {pendingVisits.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Bekleyen 2. visit yok
                  </p>
                ) : (
                  pendingVisits.map((visit) => {
                    const daysSince = getDaysSinceVisit(visit.visit_date);
                    const isOverdue = daysSince >= 90;
                    
                    return (
                      <div 
                        key={visit.id}
                        className={`p-3 rounded-lg border ${
                          isOverdue 
                            ? 'bg-red-50 border-red-200' 
                            : 'bg-yellow-50 border-yellow-200'
                        }`}
                        data-testid={`pending-visit-${visit.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{visit.patient_name}</span>
                              {isOverdue && (
                                <Badge variant="destructive" className="text-xs">
                                  3+ ay
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {visit.treatment_type || '-'} • {visit.visit_date || '-'}
                            </p>
                            {visit.notes && (
                              <p className="text-xs text-muted-foreground mt-1 italic">
                                Not: {visit.notes}
                              </p>
                            )}
                            {isAdmin() && (
                              <p className="text-xs text-blue-600 mt-1">
                                Personel: {visit.created_by?.split('@')[0] || '-'}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => handleUpdateStatus(visit.id, 'completed')}
                              title="Tamamlandı"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleRemoveFromSecondVisit(visit.id)}
                              title="Kaldır"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>

          {/* Completed Visits */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="font-[Manrope] flex items-center gap-2 text-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Tamamlanan 2. Visitler
                <Badge variant="outline" className="ml-auto">{completedVisits.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {completedVisits.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Tamamlanan 2. visit yok
                  </p>
                ) : (
                  completedVisits.map((visit) => (
                    <div 
                      key={visit.id}
                      className="p-3 rounded-lg bg-green-50 border border-green-200"
                      data-testid={`completed-visit-${visit.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium">{visit.patient_name}</span>
                          <p className="text-xs text-muted-foreground mt-1">
                            {visit.treatment_type || '-'} • {visit.visit_date || '-'}
                          </p>
                          {visit.notes && (
                            <p className="text-xs text-muted-foreground mt-1 italic">
                              Not: {visit.notes}
                            </p>
                          )}
                          {isAdmin() && (
                            <p className="text-xs text-blue-600 mt-1">
                              Personel: {visit.created_by?.split('@')[0] || '-'}
                            </p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                          onClick={() => handleUpdateStatus(visit.id, 'pending')}
                          title="Beklemeye Al"
                        >
                          <Clock className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Mark as 2nd Visit Dialog */}
      <Dialog open={markDialogOpen} onOpenChange={setMarkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>2. Visit Olarak İşaretle</DialogTitle>
            <DialogDescription>
              Bu hastayı 2. visit olarak işaretlemek istediğinize emin misiniz?
            </DialogDescription>
          </DialogHeader>
          
          {selectedRecord && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-2">
                  <User className="h-4 w-4 text-primary" />
                  <span className="font-medium">{selectedRecord.patient_name}</span>
                </div>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>Tedavi: {selectedRecord.treatment || '-'}</p>
                  <p>Tarih: {selectedRecord.ticket_date || selectedRecord.treatment_date || '-'}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="visit-notes">Not (Opsiyonel)</Label>
                <Textarea
                  id="visit-notes"
                  placeholder="2. visit ile ilgili not ekleyin..."
                  value={visitNotes}
                  onChange={(e) => setVisitNotes(e.target.value)}
                  rows={3}
                  data-testid="visit-notes-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkDialogOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleMarkAsSecondVisit} 
              disabled={saving}
              data-testid="confirm-mark-btn"
            >
              {saving ? 'Kaydediliyor...' : 'İşaretle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Notes Dialog */}
      <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Not Düzenle</DialogTitle>
            <DialogDescription>
              {selectedVisit?.patient_name} için not ekleyin veya düzenleyin
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2">
            <Label htmlFor="edit-notes">Not</Label>
            <Textarea
              id="edit-notes"
              placeholder="Not ekleyin..."
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              rows={4}
              data-testid="edit-notes-input"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotesDialogOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleUpdateNotes} 
              disabled={saving}
              data-testid="save-notes-btn"
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Mark Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Toplu 2. Visit İşaretleme</DialogTitle>
            <DialogDescription>
              {selectedRecords.length} hasta 2. visit olarak işaretlenecek
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-muted/50 max-h-[200px] overflow-y-auto">
              <p className="text-sm font-medium mb-2">Seçilen Hastalar:</p>
              <div className="space-y-1">
                {selectedRecords.map(id => {
                  const record = salesRecords.find(r => r.id === id);
                  return record ? (
                    <div key={id} className="text-sm text-muted-foreground flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      {record.patient_name}
                    </div>
                  ) : null;
                })}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="bulk-notes">Ortak Not (Opsiyonel)</Label>
              <Textarea
                id="bulk-notes"
                placeholder="Tüm hastalar için ortak not ekleyin..."
                value={bulkNotes}
                onChange={(e) => setBulkNotes(e.target.value)}
                rows={3}
                data-testid="bulk-notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleBulkMarkAsSecondVisit} 
              disabled={saving}
              data-testid="confirm-bulk-mark-btn"
            >
              {saving ? 'İşaretleniyor...' : `${selectedRecords.length} Hasta İşaretle`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
